import { z } from "zod";

/** The order contract's file fields: which files a create-order item carries, and under which names. */

export const ORDER_FILE_FIELDS = ["coverUrl", "dustJacketUrl", "gutsUrl"] as const;

export type OrderFileField = (typeof ORDER_FILE_FIELDS)[number];

export function isOrderFileField(key: string): key is OrderFileField {
  return ORDER_FILE_FIELDS.some((field) => field === key);
}

// The presence of a geometry block is what declares an outer file. `nullable` because a
// declared-but-null block is a catalog defect that order intake reads as absent.
const geometrySchema = z.object({
  cover_spec: z.unknown().nullable().optional(),
  jacket_spec: z.unknown().nullable().optional(),
});

/** Each geometry block names the one outer file a product printed to it is submitted with. */
const OUTER_FILE_BY_GEOMETRY_BLOCK = [
  ["cover_spec", "coverUrl"],
  ["jacket_spec", "dustJacketUrl"],
] as const;

const COVER_AND_INTERIOR: readonly OrderFileField[] = ["coverUrl", "gutsUrl"];

/**
 * The files a product's SKU declares, in the order the Create Order form offers them: an outer file
 * per geometry block the specification carries, then the interior every book takes.
 *
 * A mirror of the rule order intake enforces against the same published specification. A
 * specification carrying no geometry block at all — missing, or not the published shape — answers
 * with the cover-and-interior pair, which is what intake holds such a product to.
 */
export function declaredOrderFiles(specification: unknown): readonly OrderFileField[] {
  const parsed = geometrySchema.safeParse(specification);
  const outerFiles = OUTER_FILE_BY_GEOMETRY_BLOCK.filter(
    ([block]) => parsed.success && parsed.data[block] !== undefined && parsed.data[block] !== null
  ).map(([, field]) => field);
  return outerFiles.length > 0 ? [...outerFiles, "gutsUrl"] : COVER_AND_INTERIOR;
}
